import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'

import {
  ICreateDirectConversationRequestInterfaces,
} from '@toolify/server/src/controllers/ChatController/validators/CreateDirectConversation/types/ICreateDirectConversationRequestInterfaces'
import {
  IAddMembersToDirectConversationRequestInterfaces,
} from '@toolify/server/src/controllers/ChatController/validators/AddMembersToDirectConversation/types/IAddMembersToDirectConversationRequestInterfaces'
import {IEntity} from '@toolify/server/src/services/EntityService/types/IEntity'
import {
  IForkDirectConversationRequestInterfaces,
} from '@toolify/server/src/controllers/ChatController/validators/ForkDirectConversation/types/IForkDirectConversationRequestInterfaces'
import {
  IGetDirectConversationRequestInterfaces,
} from '@toolify/server/src/controllers/ChatController/validators/GetDirectConversation/types/IGetDirectConversationRequestInterfaces'
import {
  mapDirectConversationResponseInstance,
} from '@toolify/client/src/modules/api/singletons/mapDirectConversationResponseInstance'
import {
  IGetDirectConversationsRequestInterfaces,
} from '@toolify/server/src/controllers/ChatController/validators/GetDirectConversations/types/IGetDirectConversationsRequestInterfaces'
import {
  ISetDirectConversationArchivedRequestInterfaces,
} from '@toolify/server/src/controllers/ChatController/validators/SetDirectConversationArchived/types/ISetDirectConversationArchivedRequestInterfaces'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IApiStoreRequestOptions} from '@toolify/client/src/stores/ApiRequestStore/types/IApiStoreRequestOptions'
import {getDirectConversationForbiddenMessageKey} from '@toolify/client/src/modules/chat/functions/getDirectConversationForbiddenMessageKey'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {i18n} from '@toolify/client/src/setup/i18n'

const messages = useMessages()

async function withAgentMentionForbiddenMessage<T>(
  entities: IEntity[],
  request: (options: IApiStoreRequestOptions) => Promise<T>,
): Promise<T> {
  const hideNoPermissionsToast = entities.some(entity => entity.entityType === EntityType.Agent)
  try {
    return await request({hideNoPermissionsToast})
  } catch(error) {
    const messageKey = hideNoPermissionsToast ? getDirectConversationForbiddenMessageKey(error) : null
    if(messageKey) {
      messages.error(i18n.global.t(messageKey), {collapseKey: 'permission.denied'})
    }
    throw error
  }
}

export const useDirectConversationApiStore = defineStore('directConversationApiStore', {
  actions: {
    async forkDirectConversation(
      directConversationId: string,
      messageId: string,
      name: string | null,
    ): Promise<IForkDirectConversationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {directConversation} = await apiRequestStore.post<IForkDirectConversationRequestInterfaces>(
        `/direct-conversation/${directConversationId}/fork`,
        {
          data: {
            messageId,
            name,
          },
        },
      )
      return {
        directConversation: mapDirectConversationResponseInstance(directConversation),
      }
    },
    async createDirectConversation(
      entities: IEntity[],
      options: Pick<ICreateDirectConversationRequestInterfaces['body'], 'forceNew'> = {},
    ): Promise<ICreateDirectConversationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {directConversation} = await withAgentMentionForbiddenMessage(entities, requestOptions => {
        return apiRequestStore.post<ICreateDirectConversationRequestInterfaces>('/direct-conversation', {
          data: {
            entities,
            forceNew: options.forceNew,
          },
        }, requestOptions)
      })
      return {
        directConversation: mapDirectConversationResponseInstance(directConversation),
      }
    },

    async getDirectConversation(
      directConversationId: string,
    ): Promise<IGetDirectConversationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {directConversation} = await apiRequestStore.get<IGetDirectConversationRequestInterfaces>(`/direct-conversation/${directConversationId}`)
      return {
        directConversation: mapDirectConversationResponseInstance(directConversation),
      }
    },

    async getDirectConversations(): Promise<IGetDirectConversationsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {directConversations} = await apiRequestStore.get<IGetDirectConversationsRequestInterfaces>('/direct-conversations')
      return {
        directConversations: directConversations.map(directConversation => {
          return mapDirectConversationResponseInstance(directConversation)
        }),
      }
    },

    async updateDirectConversationName(
      directConversationId: string,
      name: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put(`/direct-conversation/${directConversationId}/name`, {
        data: {
          name,
        },
      })
    },

    async setDirectConversationArchived(
      directConversationId: string,
      isArchived: boolean,
    ): Promise<ISetDirectConversationArchivedRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const response = await apiRequestStore.post<ISetDirectConversationArchivedRequestInterfaces>(
        `/direct-conversation/${directConversationId}/archived`,
        {
          data: {
            isArchived,
          },
        },
      )
      return {
        archivedAt: response.archivedAt ? new Date(response.archivedAt) : null,
      }
    },

    async addMembersToDirectConversation(
      directConversationId: string,
      entities: IEntity[],
    ): Promise<IAddMembersToDirectConversationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {directConversation} = await withAgentMentionForbiddenMessage(entities, requestOptions => {
        return apiRequestStore.post<IAddMembersToDirectConversationRequestInterfaces>(
          `/direct-conversation/${directConversationId}/members`,
          {
            data: {
              entities,
            },
          },
          requestOptions,
        )
      })
      return {
        directConversation: mapDirectConversationResponseInstance(directConversation),
      }
    },
  },
})
